/*
 *  KAN_Utils.js
 *  Project: Kanoogi Library System
 *
 *  Created by Chris Taylor on 07/04/17
 *  Copyright © 2017-2026 Kanoogi Inc. | All Rights Reserved
 *
 */

"use strict";

import { DecodeByteText } from './KAN_Binary.js';

import * as browdet from './KAN_BrowDet.js';

// First, let's check to see if we have a console to output to, otherwise we will evaluate utils.Reverbolate statements to an empty function.
// This is required for machines without firebug or machines that are not running the developer console.
// $ Note: We will most likely put this in a better place that cleans up or disables any debug functionality.

// Is this still useful?  Has the world evolved past the need to include this check?

//if (typeof console==="undefined" || typeof console.log==="undefined")
//	{
//	console={};
//	console.log=function(){};
//	}

// Utils holds a random handful of generic and useful functions. Over time these might get grouped, sorted or organized into a different
// structure, but for now, I've just thrown them all into this one UTILS class. For no other reason than to just keep them in one place.

export function KAN_Utils()
	{
	this.safari=false;
	this.chrome=false;
	this.ie=false;
	this.firefox=false;
	this.opera=false;
	this.Mozilla=false;

	const runningLocally=location.href.includes("localhost")||location.href.includes("127.0.0.1");
	let logging=runningLocally;											// Can be switched on or off

	/* ---------------------------------------------------------------- */

	// KanoogiTextLogo() - Displays the Kanoogi name in ASCII just to make the start up sequence more fun and interesting, nothing more
	// NOTE: This looks correct when it's displayed in the console.

	this.KanoogiTextLogo=function()
		{
		const colform="color:rgb(0,255,0);";

		console.log("%c _  __                             _ ",colform);
		console.log("%c| |/ /__ _ _ __   ___   ___   __ _(_)",colform);
		console.log("%c| ' // _` | '_ \\ / _ \\ / _ \\ / _` | |",colform);
		console.log("%c| . \\ (_| | | | | (_) | (_) | (_| | |",colform);
		console.log("%c|_|\\_\\__,_|_| |_|\\___/ \\___/ \\__, |_|",colform);
		console.log("%c                             |___/   ",colform);
		console.log("TAKING INDIE GAMING TO THE NEXT QUANTUM LEVEL");
		console.log("---------------------------------------------");
		console.log("Kanoogi Inc. | All Rights Reserved | 2017-2026");
		console.log("");
		}
	/* ---------------------------------------------------------------- */

	// DebugOnly() - Outputs a console.log only if the code is running locally by default.
	// Can be switched on remotely and debug text can be displayed on main page as overlay (TO BE DEVELOPED)

	this.DebugOnly=function(message)
		{
		if (logging)
			console.dir(`Debug local: ${JSON.stringify(message)}`);
		}
	/* ---------------------------------------------------------------- */

	// GetID() - Returns the element ID if there is one in the document

	this.GetID=function(id)
		{
		return(document.getElementById(id));
		}
	/* ---------------------------------------------------------------- */

	const outscol='#00FF00'												// Green
	const greatcol='#89ff5e';											// Lightist Green
	const goodcol='#51FF35';											// Light Green
	const okcol='#B8E4B1';												// Really light green
	const badcol='#FF5050';												// Red

	// PasswordStrength() - Provides feedback on the strength of a users password using regular expressions
	// NOTE: Not sure if returning a string and color is the best solution, but it's okay for now

	this.PasswordStrength=function(password)
		{
		const weakRegex=/^(?=.*[a-z])(?=.*[A-Z]).{6,}$/;
		const mediumRegex=/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
		const strongRegex=/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[a-zA-Z\d!@#$%^&*()_+]{10,}$/;
		const outstandingRegex=/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{15,}$/;

		if (password.length<5) return({s:'Too short',c:badcol,p:false});
		else if (outstandingRegex.test(password)) return({s:'Outstanding!',c:outscol,p:true});
		else if (strongRegex.test(password)) return({s:'Great!',c:greatcol,p:true});
		else if (mediumRegex.test(password)) return({s:'Good',c:goodcol,p:true});
		else if (weakRegex.test(password)) return({s:'Passable',c:okcol,p:true});

		return({s:'hrmm',c:badcol,p:false});
		}
	/* ---------------------------------------------------------------- */

	// TouchDevice() - Returns true if the device has touch input capabilities
	// NOTE: I do not believe this is tested

	this.TouchDevice=function()
		{
		return (('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0));
		}
	/* ---------------------------------------------------------------- */

	// FetchSecure() - Use fetch to get file with the required JWT Token, redirect if fails

	this.FetchSecure=async function(filename, token)
		{
		try
			{
			const response=await fetch(filename,{headers:{'Content-Type':'application/json','Authorization':`Bearer ${token}`}});

			if (!response.ok)
				throw new Error(`FetchSecure():HTTP Error: ${response.status}`);

			if (response.redirected)
				window.location.href=response.url;

			return(await response.json());
			}
		catch (err)
			{
			console.error('FetchSecure():Error requesting file:',filename,' error:',err);
			return(null);
			}

		};
	/* ---------------------------------------------------------------- */

	// CirclesTouching() - This is supposed to be a fast collision detection system using circles

//	var xd,yd;															// Allocate these statically to save memory overhead time
//	var d1,d2;
//
//	function CirclesTouching(x1, y1, r1, x2, y2, r2)
//		{
//		xd=x1-x2; yd=y1-y2;			// Get delta distances
//		d1=(xd*xd)+(yd*yd);			// Get Distance Squared
//		d2=(r1+r2)*(r1+r2);			// Add and square the radii
//		if (d1<d2) return(true);	// Test for overlap
//		return(false);
//		};
	/* ---------------------------------------------------------------- */

	// VerifyEmailAddress() - Verifies that an email has all the basic components
	// Example - Christaylor98072@gmail.com - Includes base name, @ symbol and then domain after '.'

	this.VerifyEmailAddress=function(email)
		{
		// return(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email));


		return(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email));
		};
	/* ---------------------------------------------------------------- */

	// RunningLocally() - Return True if running locally, False otherwise

	this.RunningLocally=function()
		{
		return(runningLocally);
		};
	/* ---------------------------------------------------------------- */

	this.TextEncode=function(text)
		{
		const newbuff=new Uint8Array(text.length);						// Create a buffer for encoded text

		for (let i=0; i<text.length; i++)
			newbuff[i]=text.charCodeAt(i);

		return(newbuff);
		};
	/* ---------------------------------------------------------------- */

	this.EncodeSha256=async function(message)
		{
		// let msgBuffer=new TextEncoder('utf-8').encode(message);						// encode as UTF-8

		try
			{
			const msgBuffer=this.TextEncode(message);
			const hashBuffer=await crypto.subtle.digest('SHA-256',msgBuffer);			// hash the message

			const hashArray=Array.from(new Uint8Array(hashBuffer));						// convert ArrayBuffer to Array
			const hashHex=hashArray.map(b=>('00'+b.toString(16)).slice(-2)).join('');	// convert bytes to hex string

			return(hashHex);
			}
		catch (err)
			{
			throw new Error(`EncodeShas256():ERROR:Failed to create hash message:${err}`);
			}
		}
	/* ---------------------------------------------------------------- */

	/*	Function: RectTouch()  ** UNTESTED RECENTLY **
		Checks to see if the two rectangles passed in are overlapping or touching
		If the rectangles can't be proven to not be touching, then they *must* be touching

		Parameters:
		recta - First rectangle (Object that contains: xpos, ypos, width, height)
		rectb - Second rectangle (Object that contains: xpos, ypos, width, height) */

	// NOTE: This only works for "axis aligned" rectangles

	this.RectTouch=function(recta, rectb)
		{
		if (recta.x>=rectb.x+rectb.w)									// Rect A is off to right of Rect B
			return(false);
		if (recta.x+recta.w<rectb.x)									// Rect B is off to right of Rect A
			return(false);

		if (recta.y>=rectb.ypos+rectb.h)								// Rect A is below Rect B
			return(false);
		if (recta.y+recta.h<rectb.y)									// Rect B is below Rect A
			return(false);

		return(true);	// If the code gets here, then logically they must be touching!
		};
	/* ---------------------------------------------------------------- */

	/*	Function: RectangleInside()
		Checks to see if recta is "within" the confines of rectb

		Parameters:
		recta - First rectangle (Object that contains: xpos, ypos, width, height)
		rectb - Second rectangle (Object that contains: xpos, ypos, width, height) */

//	this.RectangleInside=function(recta, rectb)
//		{
//		if (!this.RectangleTouching(recta,rectb))
//			return(false);
//
//		if (recta.xpos<rectb.xpos)
//			return(false);
//		if (recta.xpos+recta.width>=rectb.xpos+rectb.width)
//			return(false);
//
//		if (recta.ypos<rectb.ypos)
//			return(false);
//		if (recta.ypos+recta.height>=rectb.ypos+rectb.height)
//			return(false);
//
//		return(true);
//		};
	/* ---------------------------------------------------------------- */

	/*	Function: PointInRect()
		Return true if point x,y is inside of rectangle of (xpos, ypos, width, height), else false

		Parameters:
		x - x coordinate
		y - y coordinate
		rect - rectangle (Object that contains: xpos, ypos, width, height) */

	this.PointInRect=function(x, y, rect)
		{
		if (x>=rect.x && x<=rect.x+rect.w)
			{
			if (y>=rect.y && y<=rect.y+rect.h)
				return(true);
			}
		return(false);
		};
	/* ---------------------------------------------------------------- */

	/*	Function: PointInsideCircle()
		Return true if point x,y is inside of a circle defined by (x, y, circle), else false

		Parameters:
		x - X coordinate to test
		y - Y coordinate to test
		circle - A circle (Object that contains: xcenter, ycenter, radius )

		Note: Returns true if the point is inside or on the circle */

//		this.PointInsideCircle=function(x, y, circle)
//			{
//        	if ((Math.pow(x-circle.xcenter,2)+Math.pow(y-circle.ycenter,2))<=Math.pow(circle.radius,2))
//				return(true);
//
//			return(false);
//			};
	/* ---------------------------------------------------------------- */

	/*	Function: GetRect()
		Gets the rectangle data out of the object and returns a new object with that data

		Parameters:
		recta - First rectangle

		Returns:
		rect - Object that contains: xpos, ypos, width, height */
//
//	this.GetRect=function(src)
//		{
//		var rect={};
//		rect.xpos=src.xpos;
//		rect.ypos=src.ypos;
//		rect.width=src.width;
//		rect.height=src.height;
//		return(rect);
//		};
//
//	/*	Function: PutRect()
//		Pulls the rectangle data out of Object src, and puts it into Object dst
//
//		Parameters:
//		src - Source rectangle (Object that contains: xpos, ypos, width, height)
//		dst - Destination rectangle (Object that contains: xpos, ypos, width, height) */
//
//	this.PutRect=function(src, dst)
//		{
//		dst.xpos=src.xpos;
//		dst.ypos=src.ypos;
//		dst.width=src.width;
//		dst.height=src.height;
//		};
	/* ---------------------------------------------------------------- */

	/*	Function: Elapsed()
		Converts the time difference into a string in this format MM:SS.UUU, where MM is minutes, SS is seconds and UUU is Milleseconds

		Parameters:
		time1 - start time
		time2 - end time

		Returns:
		string (minutes:seconds:milleseconds) */

//	this.Elapsed=function(time1, time2)
//		{
//		var elapsed=Math.abs(time1-time2);								// Compute time elapsed in milleseconds
//
//		var minutes=Math.floor(elapsed/60000);							// Compute the minutes elapsed
//		elapsed-=minutes*60000;
//
//		var seconds=Math.floor(elapsed/1000);
//		elapsed-=seconds*1000;
//
////		console.log("Timenow:"+now+" elapsed:"+(now-then)+" minutes:"+minutes+" seconds:"+seconds+" tenths:"+elapsed);
//
//		return(minutes+":"+seconds+"."+elapsed);
//		};
//
//	var starttime=Date.now();	// Take a snapshot of the time when the app starts up

	/* ---------------------------------------------------------------- */

//	this.ElapsedSinceStart=function()
//		{
//		return(Date.now()-starttime);
//		};
	/* ---------------------------------------------------------------- */

	// GetElapsedTime() - Takes a UTC timestamp, and returns a string with the time elapsed

	this.GetElapsedTime=function(utcTimestamp)
		{
		const utcTimestampMs=Date.parse(utcTimestamp);
		if (isNaN(utcTimestampMs)) return('Just now');

		const elapsedMilliseconds=Date.now()-utcTimestampMs;

		const seconds=Math.floor(elapsedMilliseconds/1000);
		const minutes=Math.floor(seconds/60);
		const hours=Math.floor(minutes/60);
		const days=Math.floor(hours/24);

		if (days>0) return(`${days} day${days>1?'s':''} ago`);
		if (hours>0) return(`${hours} hour${hours>1?'s': ''} ago`);
		if (minutes>0) return(`${minutes} minute${minutes>1?'s':''} ago`);
		if (seconds<5) return('Just now');

		return(`${seconds} second${seconds>1?'s':''} ago`);
		}
	/* ---------------------------------------------------------------- */

	// GetDateAndElapsed() - Takes the UTC and returns a formatted date and the current time elapsed as a string

	this.GetDateAndElapsed=function(utcTimestamp)
		{
		const elapsed=this.GetElapsedTime(utcTimestamp);
		const localDate=new Date(utcTimestamp);
		const timenow=localDate.toLocaleString(undefined,
			{
			weekday:'long',
			year:'numeric',
			year:'numeric',
			month:'long',
			day:'numeric',
			hour:'numeric',
			minute:'numeric',
			hour12:true													// Use 12-hour clock (AM/PM)
			});

		return(`${timenow} - ${elapsed}`);
		}
	/* ---------------------------------------------------------------- */

	/*	Function: PointNearRect()
		Return true if point x,y is within a margin (default 6) pixels of the rectangle, else false

		Parameters:
		x - x position
		y - y position
		rect - Rectangle (Object that contains: xpos, ypos, width, height)
		margin - Pixel width to consider when calculating proximity

		Returns:
		true or false */

	this.PointNearRect=function(x, y, rect, margin)
		{
		if (margin==null)
			margin=0;

		if (x>=rect.xpos-margin && x<=rect.xpos+rect.width+margin)
			{
			if (y>=rect.ypos-margin && y<=rect.ypos+rect.height+margin)
				return(true);
			}
		return(false);
		};
	/* ---------------------------------------------------------------- */

	/*	Function: SetCookie()
		Create/Set a Cookie. What's interesting here, is that it's so damn involved and peculiar. You would think that
		JS would have some clever and simple way to manage it. Perhaps that's because it was bolted on to HTML?

		Parameters:
		name - Name of the cookie
		value - Value to set the cookie to
		days - Days until this cookie expires */

	this.SetCookie=function(name, value, days)
		{
		let expires="";

		if (days)
			{
			const date=new Date();
			date.setTime(Date.now()+(days*24*60*60*1000));
			expires="; expires="+date.toGMTString();
			}

		document.cookie=name+"="+value+expires+"; path=/";
		};
	/* ---------------------------------------------------------------- */

	/*	Function: GetCookie()
		Gets the value of the specified cookie

		Parameters:
		name - String, name of the cookie

		Returns:
		value - Value of the cookie */

	this.GetCookie=function(name)
		{
		const nameEQ=name+"=";
		let ca=document.cookie.split(';');

		for (let i=0; i<ca.length; i++)
			{
			let c=ca[i];

			while (c.charAt(0)==' ')
				c=c.substring(1,c.length);

			if (c.indexOf(nameEQ)==0)
				return(c.substring(nameEQ.length,c.length));
			}
		return(null);
		};
	/* ---------------------------------------------------------------- */

	/*	Function: EraseCookie()
		Erase/Remove/Delete a Cookie of name

		Parameters:
		name - string, name of the cookie
		*/

	this.EraseCookie=function(name)
		{
		this.CreateCookie(name,"",-1);
		};
	/* ---------------------------------------------------------------- */

	/*	Function: ParseInput()
		Breaks the input string down into an array of key->value pairs

		Parameters:
		input - input string

		Returns:
		Array - All values parsed */

//	this.ParseInput=function(input)
//		{
//		var vals=input.split(",");
//		var destarray=[];
//
//		for (var i=0; i<vals.length; i++)
//			{
//			var temp=vals[i].split(":");
//
//			destarray[temp[0]]=temp[1];
//			destarray.push(temp);
//			}
//		return(destarray);
//		};
	/* ---------------------------------------------------------------- */

	/*	Function: ValuesClose()
		Test to see if the two values are within range of each other

		Parameters:
		val1 - First value
		val2 - Seconds value
		range - Range value

		Returns:
		true or false */

//	 this.ValuesClose=function(val1, val2, range)
//		{
//		if (Math.abs(val1-val2)<range)
//			return(true);
//
//		return(false);
//		};
	/* ---------------------------------------------------------------- */

	/*	Function: IsNumber()
		Checks to see if parameter passed in is a number

		Parameters:
		checknum - value to see if it is a number

		Returns:
		true or false */

//	this.IsNumber=function(checknum)
//		{
//		return(!isNaN(parseFloat(checknum)) && isFinite(checknum));
//		};
	/* ---------------------------------------------------------------- */

	/*	Function: MakeHexString2C()
		Takes a decimal number and converts it into a padded, 2 Character (2C) hexadecimal string

		Parameters:
		decval - decimal number to convert to a 2 character hexadecimal string

		Returns:
		Two character string - ie, 00, 0A, 10, FF, etc. */

//	function MakeHexString2C(decval)
//		{
//		var result=decval.toString(16);
//		if (decval<16) result="0"+result;
//
//		return(result);
//		}
//	this.MakeHexString2C=MakeHexString2C;
	/* ---------------------------------------------------------------- */

	/*	Function: MakeHexString4C()
		Takes a decimal number and converts it into a padded, 4 Character (4C) hexadecimal string

		Parameters:
		decval - decimal number to convert to a 4 character hexadecimal string

		Returns:
		Four character string - ie, 0000, FFFF, 0FAB, 102F, etc. */

//	this.MakeHexString4C=function(decval)
//		{
//		var result=decval.toString(16);
//
//		if (decval<16) result="000"+result;
//		else if (decval<256) result="00"+result;
//		else if (decval<4096) result="0"+result;
//
//		return(result);
//		};
	/* ---------------------------------------------------------------- */

	/*	Function: DumpObject()
		Dumps all of the objects contained in the source object out to the console

		Parameters:
		name - name of object
		target_obj - target object */

//	this.DumpObject=function(name, target_obj)
//		{
//		var contents=name+" --> ";
//
//		for (name in target_obj)
//			contents+=name+" ";
//		this.ReverboSYS(contents);
//		};
	/* ---------------------------------------------------------------- */

	/*	Function: ParseFloatNan()
		Special ParseFloat function that turns NaN into the provided default value

		Parameters:
		parseval - Value to parse
		defaultval - Value to substitute in if parseval invalid

		Returns:
		value - Parsed value or default value */

//	this.ParseFloatNan=function(parseval, defaultval)
//		{
//		var parsed=parseFloat(parseval);
//		if (isNaN(parsed))
//			parsed=defaultval;
//		return(parsed);
//		};
	/* ---------------------------------------------------------------- */

	/*	Function: MakeStyleString()
		Creates an RGB style string for canvas draw operations, no alpha (uses optimized hex format)

		Parameters:
		r - Red Value
		g - Green Value
		b - Blue Value

		Returns:
		String - Correctly formatted string, ie, "#FFEA1B" */

//	function MakeStyleString(r, g, b)
//		{
//		r=Math.floor(r);
//		g=Math.floor(g);
//		b=Math.floor(b);
//
//		return("#"+MakeHexString2C(r)+MakeHexString2C(g)+MakeHexString2C(b));
//		}
//	this.MakeStyleString=MakeStyleString;						// Public version
	/* ---------------------------------------------------------------- */

	/*	Function: MakeStyleStringA()
		Creates a RGBA style string for canvas draw operations with alpha (uses non-optimal ASC format)

		Parameters:
		r - Red Value
		g - Green Value
		b - Blue Value
		a - Alpha Value

		Returns:
		String - Correctly formatted string, ie, "rgba(234,119,46)" */

//	function MakeStyleStringA(r, g, b, a)
//		{
//		return("rgba("+r+","+g+","+b+","+a+")");
//		}
//	this.MakeStyleStringA=MakeStyleStringA;						// Public version
	/* ---------------------------------------------------------------- */

	/*	Function: CalcGrayScale()
		Takes either a #XXXXXX formatted string or a rgb(R,G,B) string, and returns a gray scale style formatted as #XXXXXX

		Parameters:
		s - String that is in either of the two popular formats

		Returns:
		String - A gray scale equivalent of the source color string */

//	this.CalcGrayScale=function(s)
//		{
//		var gray;
//
//		if (s.substring(0,1)=="#")		// We've got a string formatted like this, "#FFFFFF", which is great
//			{
//			var r=parseInt(s.substring(1,3),16);
//			var g=parseInt(s.substring(3,5),16);
//			var b=parseInt(s.substring(5,7),16);
//
//			gray=((r*0.21)+(g*0.71)+(b*0.07))/3;
//			}
//		else							// We've likely got a string formatted like this, "rgba(255,255,255,0.5)", which is more complex to decode
//			gray=128;					// So for now, we'll just return an average, probably work fine short term
//
//		return(MakeStyleString(gray,gray,gray));
//		};
	/* ---------------------------------------------------------------- */

	/*	Function: CloneObject()
		Takes an object A, creates and returns an identical object B

		Parameters:
		target - Object A, which can be any object containing any sub objects or arrays, etc.

		Returns:
		Object B - A cloned object of whatever the target object is */

//	this.CloneObject=function(target)
//		{
//		var dest=(target instanceof Array)?[]:{};
//
//		for (var i in target)
//			{
//			if (target[i] && typeof target[i]=="object")
//				dest[i]=this.CloneObject(target[i]);
//			else
//				dest[i]=target[i];
//			}
//		return(dest);
//
////		return(JSON.parse(JSON.stringify(obja)));		// This is what used to pass as clone code... this might be a good idea again?
//		};
	/* ---------------------------------------------------------------- */

	/*	Function: PrependZeroes()
		Prepend a set number of zeroes to a number. "Wish Javascript had printf" - JB

		Parameters:
		num - number to prepend
		count - number of zeros to pad out to

		Returns:
		String - The padded string */

//	this.PrependZeroes=function(num, count)
//		{
//		var numZeropad=num+''; 		// Turn into string
//
//		while (numZeropad.length<count)
//			{
//			numZeropad="0"+numZeropad;
//			}
//
//		return(numZeropad);
//		}
	/* ---------------------------------------------------------------- */

//	/*	Function: TextToImage()
//		Converts an image in textbased format (some whacky internal HTML5 format) and makes it into an Image Object
//
//		Parameters:
//		srctext - String, source of image, without the file name
//		callback - Callback function name
//		filename - String, name of the file */
//
//	this.TextToImage=function(srctext, callback, filename)
//		{
//		var newimage=new Image();
//		newimage.callback=callback;
//		newimage.src=srctext;
//		newimage.onload=function(event)
//			{
//			console.log("UTILS:TextToImage(): Filename:"+filename+" Width: "+this.width+" Height: "+this.height);
//			if (callback)
//				callback(this,filename);
//			};
//		};
//
//	/*	Function: TextToAudio()
//		Converts a sound in textbased B64 format (some whacky internal HTML5 format) and makes it into an Audio Object
//		Added by: Nick LaCroix
//
//		Parameters:
//		srctext - String, source of sound, without the file name
//		callback - Callback function
//		filename - String, name of the file */
//
//	this.TextToAudio=function(srctext, callback, filename)
//		{
//		var newsound=new Audio(srctext);
//		newsound.addEventListener("canplaythrough",function()
//			{
//			console.log("UTILS:TextToAudio(): Filename:"+filename+" Duration: "+this.duration);
//			if (callback)
//				callback(this,filename);
//			},
//		false);
//		};
	/* ---------------------------------------------------------------- */

	// TextEncode() - Convert a regular JS string into a Uint8Array with byte a seperate character code, simple enough when it's just UTF-8
	// NOTE: Could use native implementation, but it is not yet available on Microsoft Edge.  Sigh.  Thanks MS you dipshits.

	this.TextEncode=function(text)
		{
		let newbuff=new Uint8Array(text.length);					// Create a buffer for encoded text

		for (let i=0; i<text.length; i++)
			newbuff[i]=text.charCodeAt(i);

		return(newbuff);
		};
	/* ---------------------------------------------------------------- */

	// TextDecode(data, start?, end?) - Decode legacy byte text within an ArrayBuffer or bounded view

	this.TextDecode=DecodeByteText;
	/* ---------------------------------------------------------------- */

	// CrossProduct() - Computes the cross product of two points

	function CrossProduct(p1, p2)
		{
		return((p1.x*p2.y)-(p1.y*p2.x));
		}
	/* ---------------------------------------------------------------- */

	// EqualPoints() - Checks to make sure the points are the same

	function EqualPoints(p1, p2)
		{
		return((p1.x==p2.x) && (p1.y==p2.y));
		}
	/* ---------------------------------------------------------------- */

	// Equal() - Check to see if all arguments are equal to the first one

	function Equal(args)											// ALARM!! - This seems like a typo!!
		{
		const val=arguments[0];

		for (let i=1; i<arguments.length; i++)
			{
			if (arguments[i]!=val)
				return(false);
			}

		return(true);
		}
	/* ---------------------------------------------------------------- */

	// SegsCross() - This uses a cross product approach
	// NOTE: Found this on the internet, but can'

	this.SegsCross=function(p, p2, q, q2)
		{
		const r={x:p2.x-p.x,y:p2.y-p.y};							// Subtract points
		const s={x:q2.x-q.x,y:q2.y-q.y};
		const v={x:q.x-p.x,y:q.y-p.y};

		const num=CrossProduct(v,r);
		const den=CrossProduct(r,s);

		if (num==0 && den==0)										// Collinear
			{
			if (p.x==q.x && p.y==q.y) return(true);
			if (p.x==q2.x && p.y==q2.y) return(true);
			if (p2.x==q.x && p2.y==q.y) return(true);
			if (p2.x==q2.x && p2.y==q2.y) return(true);

			// Do they overlap? (Are all the point differences in either direction the same sign)

			return(!Equal((q.x - p.x < 0),(q.x - p2.x < 0),(q2.x - p.x < 0),(q2.x - p2.x < 0)) ||
				!Equal((q.y - p.y < 0),(q.y - p2.y < 0),(q2.y - p.y < 0),(q2.y - p2.y < 0)));
			}

		if (!den)													// Parallel lines?
			return(false);

		const u=num/den;
		const t=CrossProduct(v,s)/den;

		return(t>=0) && (t<=1) && (u>=0) && (u<=1);
		};
	/* ---------------------------------------------------------------- */

	// TransformPtObj() - Transforms a single point around 0,0 by theta and returns result in object r(esult)

	this.TransformPtObj=function(x, y, theta, r=null)
		{
		const s=Math.sin(theta);
		const c=Math.cos(theta);

		r=r||{};													// Create object if none provided

		r.x=x*c-y*s;
		r.y=x*s+y*c;

		return(r);													// Return object (optional to calling function to use this)
		};
	/* ---------------------------------------------------------------- */

	// TransformPt() - Transforms a single point around 0,0 by theta
	// NOTE: I need to use an array, which is inconvenient (instead of an object) because I preserve Float32Array or whatever type is passed in

	//	x2=x1·cos(theta)-y1·sin(theta)								// Formulas left here for reference
	//	y2=x1·sin(theta)+y1·cos(theta)

	this.TransformPt=function(x, y, dst, theta)
		{
		const s=Math.sin(theta);
		const c=Math.cos(theta);

		dst[0]=x*c-y*s;
		dst[1]=x*s+y*c;
		};
	/* ---------------------------------------------------------------- */

	// TransformPts() - Takes an array of points and transforms them according to theta
	// Using a simple 2D transform

	this.TransformPts=function(pts, ptsx, theta)
		{
		const s=Math.sin(theta);
		const c=Math.cos(theta);

		for (let i=0; i<pts.length; i+=2)
			{
			ptsx[i+0]=Math.round(pts[i+0]*c-pts[i+1]*s);
			ptsx[i+1]=Math.round(pts[i+0]*s+pts[i+1]*c);
			}
		};
	/* ---------------------------------------------------------------- */

	// BrowserDetext() - Detect the browser and create some quick bools

	const browser=browdet.BrowserDetect["browser"];
	this.browser=browser;											// Make public

	if (browser=="Safari")
		this.safari=true;
	else if (browser=="Chrome")
		this.chrome=true;
	else if (browser=="Explorer")
		this.explorer=true;
	else if (browser=="Firefox")
		this.firefox=true;
	else if (browser=="Opera")
		this.opera=true;
	else if (browser=="Mozilla")
		this.mozilla=true;

	// Operating System Detection. Note: I should make this code look just like the browser code, with a this.osname.Windows=true approach.

	if (navigator.appVersion.indexOf("Win")!=-1)
		{
		this.osname="Windows";
		this.windows=true;
		}
	else
	if (navigator.appVersion.indexOf("Mac")!=-1)
		this.osname="MacOS";
	else
	if (navigator.appVersion.indexOf("X11")!=-1)
		this.osname="UNIX";
	else
	if (navigator.appVersion.indexOf("Linux")!=-1)
		this.osname="Linux";
	else
		this.osname="Unknown OS";

	if (logging)
		console.debug("Detecting Browser:"+browser+"  Running On OS:"+this.osname);	// debug (not log): browser/OS sniff trace, dev-only, hidden at default DevTools log level

this.getBrowserInfo=function()
	{
	const userAgent=navigator.userAgent;
	const matches=userAgent.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];

	if (/trident/i.test(matches[1]))
		{
		return { name: 'IE', version: (userAgent.match(/(?:rv:)+(\d+)/) || [])[1] || '' };
		}

	if (matches[1] === 'Chrome')
		{
		const temp=userAgent.match(/(?:OPR|Edge)\/(\d+)/);
		if (temp) return { name: temp[1].replace('OPR', 'Opera'), version: temp[2] };
		}

	matches.splice(1, 1, (userAgent.match(/version\/(\d+)/i) || [])[1] || '');

	return {name:matches[0],version:matches[1]};
	}

	const browserInfo=this.getBrowserInfo();
	if (logging)
		console.debug(`You are using ${browserInfo.name} version ${browserInfo.version}`);	// debug (not log): browser-version trace, dev-only
	}
